import React, { useState } from 'react';
import { authJsonHeaders, getActiveCabinetId } from '../utils/authHeaders';
import './ClientAddForm.css';

const TABS = [
  { id: 'customer', label: 'Client', icon: 'fa-building' },
  { id: 'admin', label: 'Administration', icon: 'fa-headset' },
  { id: 'manager', label: 'Manager', icon: 'fa-user-tie' },
];

const PERIODE_LABELS = {
  mensuel: 'Mensuel',
  trimestriel: 'Trimestriel',
  semestriel: 'Semestriel',
  annuel: 'Annuel',
};

const MOIS_HONO_OPTIONS = [
  { value: '1', label: '1er janvier' },
  { value: '2', label: '1er février' },
  { value: '3', label: '1er mars' },
  { value: '4', label: '1er avril' },
  { value: '5', label: '1er mai' },
  { value: '6', label: '1er juin' },
  { value: '7', label: '1er juillet' },
  { value: '8', label: '1er août' },
  { value: '9', label: '1er septembre' },
  { value: '10', label: '1er octobre' },
  { value: '11', label: '1er novembre' },
  { value: '12', label: '1er décembre' },
];

const initialForm = () => ({
  name: '',
  num_chrono: '',
  vat_code: '',
  adresse: '',
  personne_type: 'PP',
  nh_type: 'E',
  administration_email: '',
  administration_phone_number: '',
  first_name: '',
  last_name: '',
  phone_numbers: '',
  emails: '',
  identity_type: '',
  identity_number: '',
  identity_issue_date: '',
  identity_expiray_date: '',
  has_hono: false,
  periode_type: 'mensuel',
  montant: '',
  start_date: new Date().toISOString().slice(0, 10),
  mois: String(new Date().getMonth() + 1),
  trimestre: '1',
  semestre: '1',
  annee: String(new Date().getFullYear()),
});

export default function ClientAddForm({ onClientAdded, onCancel, cabinetId = null }) {
  const [activeTab, setActiveTab] = useState('customer');
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const name = formData.name.trim();
    if (!name) {
      setError('Le nom du client est obligatoire.');
      setActiveTab('customer');
      setLoading(false);
      return;
    }

    const adminEmail = formData.administration_email.trim();
    if (adminEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminEmail)) {
      setError('E-mail administration invalide.');
      setActiveTab('admin');
      setLoading(false);
      return;
    }

    const mgrEmail = formData.emails.trim();
    if (mgrEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(mgrEmail)) {
      setError('E-mail manager invalide.');
      setActiveTab('manager');
      setLoading(false);
      return;
    }

    try {
      // Prop explicite, sinon cabinet actif de la session (CGM2, etc.)
      const fromProp = Number(cabinetId);
      const targetCabinetId =
        Number.isFinite(fromProp) && fromProp > 0
          ? fromProp
          : getActiveCabinetId();
      const hasCabinet = Number.isFinite(targetCabinetId) && targetCabinetId > 0;
      const response = await fetch('/api/clients', {
        method: 'POST',
        headers: authJsonHeaders(
          hasCabinet ? { 'X-Cabinet-Id': String(targetCabinetId) } : {}
        ),
        body: JSON.stringify({
          nom: name,
          num_chrono: formData.num_chrono,
          matricule_fiscal: formData.vat_code.trim(),
          adresse: formData.adresse.trim(),
          personne_type: formData.personne_type,
          nh_type: formData.nh_type,
          ...(hasCabinet ? { cabinet_id: targetCabinetId } : {}),
          contact: {
            administration_email: adminEmail,
            administration_phone_number: formData.administration_phone_number.trim(),
          },
          manager: {
            first_name: formData.first_name.trim(),
            last_name: formData.last_name.trim(),
            phone_numbers: formData.phone_numbers.trim(),
            emails: mgrEmail,
          },
          identity: {
            identity_type: formData.identity_type,
            identity_number: formData.identity_number.trim(),
            identity_issue_date: formData.identity_issue_date,
            identity_expiray_date: formData.identity_expiray_date,
          },
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.error || data.message || 'Erreur lors de la création du client');
      }

      const clientId = data.clientId;

      // Toujours écrire is_auto : NH → 1, E → 0
      const nhRes = await fetch(`/api/clients/${clientId}/nh-type`, {
        method: 'PATCH',
        headers: authJsonHeaders(
          hasCabinet ? { 'X-Cabinet-Id': String(targetCabinetId) } : {}
        ),
        body: JSON.stringify({ nh_type: formData.nh_type || 'E', mark_configured: true }),
      });
      if (!nhRes.ok) {
        const nhData = await nhRes.json().catch(() => ({}));
        throw new Error(nhData.error || 'Erreur lors de la mise à jour NH/E (is_auto)');
      }

      if (formData.has_hono && formData.montant) {
        const montant = parseFloat(String(formData.montant).replace(',', '.'));
        let computedStartDate = formData.start_date;
        if (formData.periode_type === 'mensuel') {
          const mois = Number(formData.mois || 1);
          computedStartDate = `${formData.annee}-${String(mois).padStart(2, '0')}-01`;
        } else if (formData.periode_type === 'trimestriel') {
          const month = (parseInt(formData.trimestre, 10) - 1) * 3 + 1;
          computedStartDate = `${formData.annee}-${String(month).padStart(2, '0')}-01`;
        } else if (formData.periode_type === 'semestriel') {
          const month = formData.semestre === '1' ? '01' : '07';
          computedStartDate = `${formData.annee}-${month}-01`;
        } else if (formData.periode_type === 'annuel') {
          computedStartDate = `${formData.annee}-01-01`;
        }

        await fetch('/api/client-hono-periode', {
          method: 'PUT',
          headers: authJsonHeaders(
            hasCabinet ? { 'X-Cabinet-Id': String(targetCabinetId) } : {}
          ),
          body: JSON.stringify({
            client_id: clientId,
            periode_type: formData.periode_type,
            montant,
            start_date: computedStartDate,
          }),
        });
      }

      onClientAdded();
    } catch (err) {
      console.error('Erreur:', err);
      setError(err.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="caf">
      <form onSubmit={handleSubmit} noValidate>
        {error && (
          <div className="caf-alert" role="alert">
            <i className="fas fa-exclamation-triangle" aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}

        <nav className="caf-tabs" role="tablist" aria-label="Sections du client">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`caf-tab${activeTab === tab.id ? ' is-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className={`fas ${tab.icon}`} aria-hidden="true" />
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>

        <div className="caf-panel" role="tabpanel">
          {activeTab === 'customer' && (
            <div className="caf-grid">
              <div className="caf-field caf-field--name">
                <label htmlFor="caf-name">Nom</label>
                <input
                  id="caf-name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Nom du client"
                  autoFocus
                  required
                />
              </div>
              <div className="caf-field caf-field--chrono">
                <label htmlFor="caf-chrono">N° chrono</label>
                <input
                  id="caf-chrono"
                  name="num_chrono"
                  type="number"
                  min="0"
                  step="1"
                  value={formData.num_chrono}
                  onChange={handleChange}
                  placeholder="N°"
                />
              </div>
              <div className="caf-field">
                <label htmlFor="caf-vat">Matricule fiscal</label>
                <input
                  id="caf-vat"
                  name="vat_code"
                  type="text"
                  value={formData.vat_code}
                  onChange={handleChange}
                  placeholder="Matricule fiscal"
                />
              </div>
              <div className="caf-field caf-field--full">
                <label htmlFor="caf-adresse">Adresse</label>
                <input
                  id="caf-adresse"
                  name="adresse"
                  type="text"
                  value={formData.adresse}
                  onChange={handleChange}
                  placeholder="Adresse du client"
                />
              </div>
              <div className="caf-field">
                <span className="caf-label">Type</span>
                <div className="caf-choice">
                  <label className={`caf-chip${formData.personne_type === 'PP' ? ' is-on' : ''}`}>
                    <input
                      type="radio"
                      name="personne_type"
                      value="PP"
                      checked={formData.personne_type === 'PP'}
                      onChange={handleChange}
                    />
                    <span>Physique</span>
                  </label>
                  <label className={`caf-chip${formData.personne_type === 'PM' ? ' is-on' : ''}`}>
                    <input
                      type="radio"
                      name="personne_type"
                      value="PM"
                      checked={formData.personne_type === 'PM'}
                      onChange={handleChange}
                    />
                    <span>Morale</span>
                  </label>
                </div>
              </div>
              <div className="caf-field caf-field--full">
                <span className="caf-label">NH / E</span>
                <div className="caf-choice">
                  <label className={`caf-chip${formData.nh_type === 'NH' ? ' is-on' : ''}`}>
                    <input
                      type="radio"
                      name="nh_type"
                      value="NH"
                      checked={formData.nh_type === 'NH'}
                      onChange={handleChange}
                    />
                    <span>NH</span>
                  </label>
                  <label className={`caf-chip${formData.nh_type === 'E' ? ' is-on' : ''}`}>
                    <input
                      type="radio"
                      name="nh_type"
                      value="E"
                      checked={formData.nh_type === 'E'}
                      onChange={handleChange}
                    />
                    <span>E</span>
                  </label>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'admin' && (
            <div className="caf-grid">
              <div className="caf-field">
                <label htmlFor="caf-admin-email">E-mail</label>
                <input
                  id="caf-admin-email"
                  name="administration_email"
                  type="email"
                  value={formData.administration_email}
                  onChange={handleChange}
                  placeholder="E-mail"
                  autoFocus
                />
              </div>
              <div className="caf-field">
                <label htmlFor="caf-admin-phone">Téléphone</label>
                <input
                  id="caf-admin-phone"
                  name="administration_phone_number"
                  type="tel"
                  value={formData.administration_phone_number}
                  onChange={handleChange}
                  placeholder="Téléphone"
                />
              </div>
            </div>
          )}

          {activeTab === 'manager' && (
            <div className="caf-grid">
              <div className="caf-field">
                <label htmlFor="caf-first">Prénom</label>
                <input
                  id="caf-first"
                  name="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={handleChange}
                  placeholder="Prénom"
                  autoFocus
                />
              </div>
              <div className="caf-field">
                <label htmlFor="caf-last">Nom</label>
                <input
                  id="caf-last"
                  name="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={handleChange}
                  placeholder="Nom"
                />
              </div>
              <div className="caf-field">
                <label htmlFor="caf-mgr-phone">Téléphone</label>
                <input
                  id="caf-mgr-phone"
                  name="phone_numbers"
                  type="tel"
                  value={formData.phone_numbers}
                  onChange={handleChange}
                  placeholder="Téléphone"
                />
              </div>
              <div className="caf-field">
                <label htmlFor="caf-mgr-email">E-mail</label>
                <input
                  id="caf-mgr-email"
                  name="emails"
                  type="email"
                  value={formData.emails}
                  onChange={handleChange}
                  placeholder="E-mail"
                />
              </div>
              <div className="caf-subhead caf-field--full">Identité</div>
              <div className="caf-field">
                <label htmlFor="caf-id-type">Type</label>
                <select
                  id="caf-id-type"
                  name="identity_type"
                  value={formData.identity_type}
                  onChange={handleChange}
                >
                  <option value="">—</option>
                  <option value="NIC">CIN / NIC</option>
                  <option value="Passport">Passeport</option>
                  <option value="Other">Autre</option>
                </select>
              </div>
              <div className="caf-field">
                <label htmlFor="caf-id-number">N° identité</label>
                <input
                  id="caf-id-number"
                  name="identity_number"
                  type="text"
                  value={formData.identity_number}
                  onChange={handleChange}
                  placeholder="N° pièce"
                />
              </div>
              <div className="caf-field">
                <label htmlFor="caf-id-issue">Date délivrance</label>
                <input
                  id="caf-id-issue"
                  name="identity_issue_date"
                  type="date"
                  value={formData.identity_issue_date}
                  onChange={handleChange}
                />
              </div>
              <div className="caf-field">
                <label htmlFor="caf-id-expiry">Date expiration</label>
                <input
                  id="caf-id-expiry"
                  name="identity_expiray_date"
                  type="date"
                  value={formData.identity_expiray_date}
                  onChange={handleChange}
                />
              </div>
            </div>
          )}
        </div>

        <div className="caf-hono">
          <label className="caf-check">
            <input
              type="checkbox"
              name="has_hono"
              checked={formData.has_hono}
              onChange={handleChange}
            />
            <span>Configurer les honoraires</span>
          </label>

          {formData.has_hono && (
            <div className="caf-hono-body">
              <div className="caf-choice caf-choice--4">
                {Object.entries(PERIODE_LABELS).map(([key, label]) => (
                  <label
                    key={key}
                    className={`caf-chip${formData.periode_type === key ? ' is-on' : ''}`}
                  >
                    <input
                      type="radio"
                      name="periode_type"
                      value={key}
                      checked={formData.periode_type === key}
                      onChange={handleChange}
                    />
                    <span>{label}</span>
                  </label>
                ))}
              </div>
              <div className="caf-grid">
                <div className="caf-field">
                  <label htmlFor="caf-montant">Montant</label>
                  <input
                    id="caf-montant"
                    name="montant"
                    type="text"
                    value={formData.montant}
                    onChange={handleChange}
                    placeholder="Montant"
                  />
                </div>
                <div className="caf-field">
                  {formData.periode_type === 'mensuel' && (
                    <>
                      <label htmlFor="caf-mois">Mois de début</label>
                      <select
                        id="caf-mois"
                        name="mois"
                        value={formData.mois}
                        onChange={handleChange}
                      >
                        {MOIS_HONO_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <label htmlFor="caf-annee-m">Année</label>
                      <input
                        id="caf-annee-m"
                        name="annee"
                        type="number"
                        min="2000"
                        max="2100"
                        value={formData.annee}
                        onChange={handleChange}
                      />
                    </>
                  )}
                  {formData.periode_type === 'trimestriel' && (
                    <>
                      <label htmlFor="caf-trim">Trimestre</label>
                      <select
                        id="caf-trim"
                        name="trimestre"
                        value={formData.trimestre}
                        onChange={handleChange}
                      >
                        <option value="1">T1</option>
                        <option value="2">T2</option>
                        <option value="3">T3</option>
                        <option value="4">T4</option>
                      </select>
                      <label htmlFor="caf-annee-t">Année</label>
                      <input
                        id="caf-annee-t"
                        name="annee"
                        type="number"
                        min="2000"
                        max="2100"
                        value={formData.annee}
                        onChange={handleChange}
                      />
                    </>
                  )}
                  {formData.periode_type === 'semestriel' && (
                    <>
                      <label htmlFor="caf-sem">Semestre</label>
                      <select
                        id="caf-sem"
                        name="semestre"
                        value={formData.semestre}
                        onChange={handleChange}
                      >
                        <option value="1">S1</option>
                        <option value="2">S2</option>
                      </select>
                      <label htmlFor="caf-annee-s">Année</label>
                      <input
                        id="caf-annee-s"
                        name="annee"
                        type="number"
                        min="2000"
                        max="2100"
                        value={formData.annee}
                        onChange={handleChange}
                      />
                    </>
                  )}
                  {formData.periode_type === 'annuel' && (
                    <>
                      <label htmlFor="caf-annee-a">Année</label>
                      <input
                        id="caf-annee-a"
                        name="annee"
                        type="number"
                        min="2000"
                        max="2100"
                        value={formData.annee}
                        onChange={handleChange}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="caf-actions">
          <button type="button" className="caf-btn caf-btn--ghost" onClick={onCancel} disabled={loading}>
            Annuler
          </button>
          <button type="submit" className="caf-btn caf-btn--primary" disabled={loading}>
            {loading ? 'Ajout…' : 'Ajouter'}
          </button>
        </div>
      </form>
    </div>
  );
}
